/**
 * Shared TypeScript Type Definitions
 * Central location for all type definitions used across the application
 */

// ============================================================================
// Core Data Types
// ============================================================================

/**
 * Health metric data point (CPU, memory, battery, disk, wifi)
 */
export interface HealthMetric {
  timestamp: number;
  value: number | null;
}

/**
 * Disk-specific metric with name identifier
 */
export interface DiskMetric {
  name: string;
  data: HealthMetric[];
}

/**
 * Types of system health metrics
 */
export type MetricType = 'cpu' | 'disk' | 'battery' | 'memory' | 'wifi';

/**
 * Health status levels
 */
export type StatusLevel = 'critical' | 'warning' | 'healthy' | 'unknown';

// ============================================================================
// Service Data Types
// ============================================================================

/**
 * Generic service data wrapper with loading/error states
 */
export interface ServiceData<T = any> {
  data: T | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
  timestamp: string | number;
  expiresAt: number;
  loading?: boolean; // Legacy field for compatibility
}

/**
 * Simpler loading state for components
 */
export interface LoadingState {
  loading: boolean;
  error: string | null;
  data: any;
}

// ============================================================================
// User & Authentication
// ============================================================================

/**
 * Authenticated user information
 */

export interface AuthenticatedUser {
  authenticated: boolean;
  user: User;
}

export interface User {
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  lastLogin: string;
  createdAt: string;
  userRole: UserRoles;
}

export enum UserRoles {
  USER = 'USER',
  VIEWER = 'VIEWER',
  ADMIN = 'ADMIN',
}

/**
 * Authentication state
 */
export interface AuthState {
  currentUser: User | null;
  isAuthenticated: boolean;
  loading: boolean;
}

// ============================================================================
// Device & Network Types
// ============================================================================

/**
 * Device information
 */
export interface DeviceInfo {
  isReady: boolean;
  deviceId?: string;
  deviceName?: string;
  platform?: string;
  osVersion?: string;
}

/**
 * Zscaler device data
 */
export interface ZscalerDevice {
  id: string;
  name: string;
  healthMetrics?: {
    cpu?: HealthMetric[];
    memory?: HealthMetric[];
    battery?: HealthMetric[];
    disk?: DiskMetric[];
  };
  network?: {
    wifiMacAddress?: string;
    signalStrength?: number;
  };
}

/**
 * Zoom meeting data
 */
export interface ZoomMeeting {
  uuid: string;
  topic: string;
  start_time: string;
  duration: number;
  participants_count: number;
}

/**
 * Zoom quality of service data
 */
export interface ZoomQoS {
  participantBreakdown?: {
    meetingUuid: string;
    participants: {
      user_id: string;
      user_name: string;
      name?: string;
      overall_quality?: number;
      score?: number;
    }[];
  }[];
}

/**
 * Mist WiFi session data
 */
export interface MistSession {
  mac: string;
  ssid: string;
  signal: number;
  timestamp: number;
}

// ============================================================================
// Dashboard & UI Types
// ============================================================================

/**
 * Date range for queries
 */
export interface DateRange {
  start: string; // ISO string
  end: string; // ISO string
}

/**
 * Dashboard segment (systems, network, applications)
 */
export interface DashboardSegment {
  alerts: SegmentCard[];
  details: SegmentCard[];
}

/**
 * Card data for dashboard segments
 */
export interface SegmentCard {
  id: string;
  loading: boolean;
  error: string | null;
  data: any;
  type: string;
}

/**
 * Categorized columns for dashboard
 */
export interface CategorizedColumns {
  systems: DashboardSegment;
  network: DashboardSegment;
  applications: DashboardSegment;
}

// ============================================================================
// Context Types
// ============================================================================

/**
 * App context state
 */
export interface AppState {
  currentUser: User | null;
  activeEmail: string;
  userCache: {
    [email: string]: {
      [service: string]: ServiceData;
    };
  };
  deviceInfo: DeviceInfo;
  searchedUserName: string;
  dateRange: DateRange | null;
}

/**
 * UI context state
 */
export interface UIState {
  view: 'dashboard' | 'admin';
  searchMode: boolean;
  searchEmail: string;
  modals: {
    [key: string]: boolean;
  };
}

/**
 * Cache context state
 */
export interface CacheState {
  cache: {
    [key: string]: ServiceData;
  };
  activeRequests: {
    [key: string]: AbortController;
  };
}

// ============================================================================
// Autocomplete Types
// ============================================================================

/**
 * User suggestion for search autocomplete
 */
export interface UserSuggestion {
  email: string;
  name: string;
  department: string | null;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * API response wrapper
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Common component props
 */
export interface BaseComponentProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Card component props
 */
export interface CardProps extends BaseComponentProps {
  title?: string;
  subtitle?: string;
  loading?: boolean;
  error?: string | null;
  children?: React.ReactNode;
}
